import { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  ClipboardPaste,
  Copy,
  Download,
  FileOutput,
  RefreshCw,
  RotateCcw,
  X,
} from "lucide-react";
import { rulesListRules } from "../../gen";
import type { RuleResponse } from "../../gen/models";
import { useOrg } from "../../hooks/useOrg";
import { useToast } from "../../context/ToastContext";

type FormatMode = "instructions_only" | "with_titles" | "full_metadata";

function formatRulesText(rules: RuleResponse[], mode: FormatMode): string {
  // Sort descending by priority (higher priority number first)
  const sorted = [...rules].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));

  if (mode === "instructions_only") {
    return sorted
      .map((r) => r.instruction?.trim())
      .filter(Boolean)
      .join("\n\n");
  }

  if (mode === "with_titles") {
    return sorted
      .map((r) => `### ${r.title} (Priority: ${r.priority})\n${r.instruction?.trim()}`)
      .filter(Boolean)
      .join("\n\n");
  }

  // full_metadata
  return sorted
    .map(
      (r) =>
        `### [v${r.version} | P:${r.priority} | ${r.category.toUpperCase()}${
          r.agent_type ? ` | ${r.agent_type}` : " | universal"
        }] ${r.title}\n${r.instruction?.trim()}`
    )
    .filter(Boolean)
    .join("\n\n");
}

export function RulesExportView() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { activeOrgId } = useOrg();
  const { showToast } = useToast();

  const statusFilter = searchParams.get("status") || "all";
  const agentFilter = searchParams.get("agent") || "all";
  const categoryFilter = searchParams.get("category") || "all";
  const onlyPrivate = searchParams.get("only_private") === "true";
  const searchQuery = searchParams.get("q") || "";

  const [rules, setRules] = useState<RuleResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formatMode, setFormatMode] = useState<FormatMode>("instructions_only");
  const [textContent, setTextContent] = useState<string>("");
  const [copied, setCopied] = useState(false);
  const [isEdited, setIsEdited] = useState(false);

  const loadAllRules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const allItems: RuleResponse[] = [];
      let offset = 0;
      const limit = 200;
      let hasMore = true;

      while (hasMore) {
        const params: any = { limit, offset };
        if (statusFilter !== "all") params.status = statusFilter;
        if (agentFilter !== "all") params.agent_type = agentFilter;
        if (categoryFilter !== "all") params.category = categoryFilter;
        if (onlyPrivate) params.only_private = true;
        if (searchQuery.trim()) params.q = searchQuery.trim();

        const res = await rulesListRules(params);
        const data = (res as any).data;
        let items: RuleResponse[] = [];
        let total = 0;

        if (data && Array.isArray(data.items)) {
          items = data.items;
          total = data.total ?? data.items.length;
        } else if (Array.isArray(data)) {
          items = data;
          total = data.length;
        }

        allItems.push(...items);
        offset += limit;
        if (allItems.length >= total || items.length === 0) {
          hasMore = false;
        }
      }

      allItems.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
      setRules(allItems);
      const generated = formatRulesText(allItems, formatMode);
      setTextContent(generated);
      setIsEdited(false);
    } catch (err: any) {
      setError(err?.message || "Failed to load rules for export");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, agentFilter, categoryFilter, onlyPrivate, searchQuery, formatMode, activeOrgId]);

  useEffect(() => {
    loadAllRules();
  }, [loadAllRules]);

  const handleFormatChange = (newMode: FormatMode) => {
    setFormatMode(newMode);
    const updated = formatRulesText(rules, newMode);
    setTextContent(updated);
    setIsEdited(false);
  };

  const handleReset = () => {
    const original = formatRulesText(rules, formatMode);
    setTextContent(original);
    setIsEdited(false);
    showToast("Reset to generated rules text", { type: "info" });
  };

  const handleCopy = async () => {
    if (!textContent) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(textContent);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = textContent;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopied(true);
      showToast("Copied rules to clipboard", { type: "success" });
      setTimeout(() => setCopied(false), 2000);
    } catch (err: any) {
      showToast("Failed to copy: " + (err?.message || "Unknown error"), { type: "error" });
    }
  };

  const handlePaste = async () => {
    try {
      if (navigator?.clipboard?.readText) {
        const clipText = await navigator.clipboard.readText();
        if (clipText) {
          setTextContent(clipText);
          setIsEdited(true);
          showToast("Pasted clipboard text into editor", { type: "info" });
        } else {
          showToast("Clipboard is empty", { type: "info" });
        }
      } else {
        showToast("Clipboard read not supported by browser", { type: "warning" });
      }
    } catch (err: any) {
      showToast("Unable to read clipboard: " + (err?.message || "Permission denied"), { type: "warning" });
    }
  };

  const handleDownload = () => {
    if (!textContent) return;
    const blob = new Blob([textContent], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `rules-export-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast("Downloaded rules export file", { type: "success" });
  };

  const charCount = textContent.length;
  const wordCount = useMemo(() => {
    const trimmed = textContent.trim();
    return trimmed ? trimmed.split(/\s+/).length : 0;
  }, [textContent]);
  const lineCount = useMemo(() => {
    return textContent ? textContent.split("\n").length : 0;
  }, [textContent]);

  const activeFilters = useMemo(() => {
    const items: { label: string; value: string }[] = [];
    if (agentFilter !== "all") items.push({ label: "Agent", value: agentFilter });
    if (statusFilter !== "all") items.push({ label: "Status", value: statusFilter });
    if (categoryFilter !== "all") items.push({ label: "Category", value: categoryFilter });
    if (onlyPrivate) items.push({ label: "Visibility", value: "Private only" });
    if (searchQuery.trim()) items.push({ label: "Query", value: searchQuery.trim() });
    return items;
  }, [agentFilter, statusFilter, categoryFilter, onlyPrivate, searchQuery]);

  return (
    <div className="max-w-7xl mx-auto w-full h-full flex flex-col space-y-4">
      {/* Top Mobile Back Button & Desktop Dismiss */}
      <div className="flex items-center justify-between shrink-0">
        <button
          type="button"
          onClick={() => navigate(`/rules${location.search}`)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-white transition"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Back to directives</span>
        </button>

        <button
          type="button"
          onClick={() => navigate(`/rules${location.search}`)}
          className="p-1 rounded-lg text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 transition"
          title="Close export view"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Header Info Panel */}
      <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-5 shadow-2xs space-y-3 shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="p-1.5 rounded-lg bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300">
                <FileOutput className="w-4 h-4" />
              </div>
              <h1 className="text-base sm:text-lg font-bold tracking-tight text-stone-900 dark:text-stone-100">
                Export Operational Directives
              </h1>
              <span className="px-2 py-0.5 rounded-full text-[11px] font-mono font-bold bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 border border-stone-200 dark:border-stone-700">
                {rules.length} {rules.length === 1 ? "directive" : "directives"}
              </span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-400 dark:border-emerald-800/80">
                Sorted by priority (desc)
              </span>
            </div>
            <p className="text-xs text-stone-500 dark:text-stone-400">
              Directives sorted by priority descending and concatenated with double newlines.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 shrink-0">
            {isEdited && (
              <button
                type="button"
                onClick={handleReset}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-600 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700 text-xs font-semibold transition"
                title="Reset to generated export"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Reset</span>
              </button>
            )}
            <button
              type="button"
              onClick={handleDownload}
              disabled={loading || !textContent}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-700 text-xs font-semibold transition shadow-2xs disabled:opacity-50"
              title="Download text file"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Download</span>
            </button>
            <button
              type="button"
              onClick={handleCopy}
              disabled={loading || !textContent}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white transition shadow-2xs disabled:opacity-50"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copied ? "Copied" : "Copy"}</span>
            </button>
          </div>
        </div>

        {/* Filter Badges and Format Segmented Control */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-stone-100 dark:border-stone-800/80 text-xs">
          <div className="flex items-center gap-3 text-[11px] font-mono text-stone-500 dark:text-stone-400 flex-wrap">
            {activeFilters.length > 0 ? (
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[11px] text-stone-400 font-sans">Filters:</span>
                {activeFilters.map((f) => (
                  <span
                    key={f.label}
                    className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300 border border-stone-200 dark:border-stone-700"
                  >
                    {f.label}: {f.value}
                  </span>
                ))}
                <span>•</span>
              </div>
            ) : null}
            <span>{charCount.toLocaleString()} chars</span>
            <span>•</span>
            <span>{wordCount.toLocaleString()} words</span>
            <span>•</span>
            <span>{lineCount.toLocaleString()} lines</span>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-stone-400 dark:text-stone-500">Format:</span>
            <div className="inline-flex rounded-lg border border-stone-200 dark:border-stone-800 p-0.5 bg-stone-100/60 dark:bg-stone-950/60">
              <button
                type="button"
                onClick={() => handleFormatChange("instructions_only")}
                className={`px-2 py-0.5 rounded text-[11px] font-medium transition ${
                  formatMode === "instructions_only"
                    ? "bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 shadow-2xs font-semibold"
                    : "text-stone-500 hover:text-stone-700 dark:hover:text-stone-300"
                }`}
              >
                Directives Only
              </button>
              <button
                type="button"
                onClick={() => handleFormatChange("with_titles")}
                className={`px-2 py-0.5 rounded text-[11px] font-medium transition ${
                  formatMode === "with_titles"
                    ? "bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 shadow-2xs font-semibold"
                    : "text-stone-500 hover:text-stone-700 dark:hover:text-stone-300"
                }`}
              >
                With Titles
              </button>
              <button
                type="button"
                onClick={() => handleFormatChange("full_metadata")}
                className={`px-2 py-0.5 rounded text-[11px] font-medium transition ${
                  formatMode === "full_metadata"
                    ? "bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 shadow-2xs font-semibold"
                    : "text-stone-500 hover:text-stone-700 dark:hover:text-stone-300"
                }`}
              >
                Full Metadata
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Textarea Container with Top-Right Hover Buttons */}
      <div className="relative group rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 shadow-2xs overflow-hidden flex flex-col flex-1 min-h-[420px]">
        {/* Top-Right Corner Action Buttons on Hover */}
        <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5 opacity-90 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity bg-white/95 dark:bg-stone-900/95 backdrop-blur-xs p-1 rounded-lg border border-stone-200/90 dark:border-stone-800/90 shadow-sm">
          <button
            type="button"
            onClick={handleCopy}
            title={copied ? "Copied!" : "Copy to clipboard"}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-mono transition border ${
              copied
                ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/80 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700"
                : "bg-white dark:bg-stone-800 text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-100 dark:hover:bg-stone-700 border-stone-200 dark:border-stone-700"
            }`}
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                  Copied
                </span>
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                <span className="text-[11px]">Copy</span>
              </>
            )}
          </button>

          <button
            type="button"
            onClick={handlePaste}
            title="Paste from clipboard"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-mono bg-white dark:bg-stone-800 text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-100 dark:hover:bg-stone-700 border border-stone-200 dark:border-stone-700 transition"
          >
            <ClipboardPaste className="w-3.5 h-3.5" />
            <span className="text-[11px]">Paste</span>
          </button>
        </div>

        {/* Loading Overlay */}
        {loading ? (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/70 dark:bg-stone-900/70 backdrop-blur-2xs gap-2">
            <RefreshCw className="w-5 h-5 animate-spin text-stone-500" />
            <span className="text-xs text-stone-500 font-mono">Loading directives...</span>
          </div>
        ) : error ? (
          <div className="p-6 text-center text-xs text-rose-600 dark:text-rose-400">
            {error}
          </div>
        ) : null}

        {/* Textarea */}
        <textarea
          value={textContent}
          onChange={(e) => {
            setTextContent(e.target.value);
            setIsEdited(true);
          }}
          spellCheck={false}
          placeholder={loading ? "Loading directives..." : "No directives matching current filters."}
          className="w-full flex-1 p-5 font-mono text-xs sm:text-sm leading-relaxed bg-transparent text-stone-800 dark:text-stone-200 resize-none focus:outline-hidden [scrollbar-gutter:stable] selection:bg-indigo-200 dark:selection:bg-indigo-800 selection:text-stone-900 dark:selection:text-stone-100"
        />
      </div>
    </div>
  );
}

